import { useCallback, useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { getResolvedWorkspace } from "../providers/supabase/workspaceConfig";
import { callNylasApi } from "../providers/supabase/nylasApi";

/**
 * Settings → My profile - connect YOUR OWN Google/Microsoft account via
 * Nylas. Personal, not workspace-wide: each sales rep connects their own
 * account, one grant per user (apps/services/src/modules/nylas), covering
 * BOTH calendar (read-only) and email sending (decision #42's marketing
 * transport) in one consent. Composing/sending itself lives elsewhere -
 * this card only covers connect/disconnect and shows which account is
 * linked.
 */

interface Connection {
  provider: string;
  email: string | null;
}

export const CalendarConnectionCard = () => {
  const workspace = getResolvedWorkspace();
  const servicesUrl = (import.meta.env.VITE_CONTROL_PLANE_URL ?? "").replace(/\/+$/, "");
  const slug = workspace?.slug;

  const [connection, setConnection] = useState<Connection | null | "loading">("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justConnected, setJustConnected] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data, error: err } = await getSupabaseClient()
        .from("calendar_connections")
        .select("provider,email")
        .limit(1)
        .maybeSingle();
      if (err) setError(err.message);
      setConnection((data as Connection | null) ?? null);
    } catch (e) {
      // Whatever went wrong, land on a definite state - never leave the
      // card stuck showing "Loading…" forever with no way to recover.
      setError(e instanceof Error ? e.message : String(e));
      setConnection(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("nylas") === "connected") {
      setJustConnected(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async (provider: "google" | "microsoft") => {
    setBusy(true);
    setError(null);
    try {
      const result = await callNylasApi<{ url: string }>("/integrations/nylas/connect-url", {
        params: { provider },
      });
      if (!result) throw new Error("Not signed in.");
      window.location.href = result.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const sendTestEmail = async () => {
    if (connection === "loading" || !connection?.email) return;
    setBusy(true);
    setError(null);
    setTestResult(null);
    try {
      await callNylasApi("/integrations/nylas/send", {
        method: "POST",
        body: {
          to: [{ email: connection.email }],
          subject: "Obvious CRM - test email",
          body: "<p>This confirms your connected account can send email through Obvious CRM.</p>",
        },
      });
      setTestResult(`Sent - check ${connection.email}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setError(null);
    setTestResult(null);
    try {
      await callNylasApi("/integrations/nylas/disconnect", { method: "POST" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!slug || !servicesUrl) {
    return (
      <Card>
        <CardContent className="space-y-2">
          <h2 className="text-xl font-semibold text-muted-foreground">Calendar</h2>
          <p className="text-sm text-muted-foreground">
            Calendar connect is available in deployed workspaces only - the
            demo preview has no calendar of its own to connect.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold text-muted-foreground">Calendar & email</h2>
        </div>
        {error && (
          <p role="alert" className="text-sm text-[#c25d5d]">
            {error}
          </p>
        )}
        {justConnected && !error && (
          <p className="text-sm text-[#6a8f6b]">Account connected.</p>
        )}
        {connection === "loading" ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : connection ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Connected as <span className="text-foreground font-medium">{connection.email}</span>{" "}
                ({connection.provider}) - calendar and sending
              </p>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void sendTestEmail()}
                >
                  {busy ? "Sending…" : "Send test email"}
                </Button>
                <Button variant="outline" size="sm" disabled={busy} onClick={() => void disconnect()}>
                  {busy ? "Disconnecting…" : "Disconnect"}
                </Button>
              </div>
            </div>
            {testResult && <p className="text-sm text-[#6a8f6b]">{testResult}</p>}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Connect your Google or Microsoft account - just yours, not the
              whole workspace. Lets you see your calendar here and send email
              through your own inbox.
            </p>
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => void connect("google")}>
                {busy ? "Connecting…" : "Google"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => void connect("microsoft")}
              >
                {busy ? "Connecting…" : "Microsoft"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
